import type { Requirement } from "@sdcp/api-client";
import { useEffect, useRef, useState } from "react";

import { VerityMark } from "./Header";

export interface TaskStripProps {
  /** The whole list. The strip decides what belongs in it. */
  requirements: Requirement[];
}

/** How long a landed job stays in the strip before it leaves. */
export const HOLD_MS = 4000;

/** Retrieval happening right now. Exported so the poll in App asks the same question. */
export function isInFlight(req: Requirement): boolean {
  return req.state === "retrieving" || req.state === "verifying";
}

/**
 * The words the tray says, from the locked vocabulary (platform §19).
 *
 * The tray narrates *collection*. A job's terminal word is COLLECTED, in green,
 * and the purple VERIFIED claim never appears here (FR-14): verified is what the
 * machine concluded, and the case file card is where that claim is made. A job
 * that ended any other way — a retrieval that failed, one that fell back to the
 * borrower, one statement of two — is now the borrower's move, and the word for
 * that is YOUR TURN. One word per owner.
 */
function word(req: Requirement): string {
  switch (req.state) {
    case "retrieving":
      return "retrieving";
    case "verifying":
      return "checking";
    case "verified":
      return "collected";
    default:
      return "your turn";
  }
}

interface Held {
  requirement: Requirement;
  until: number;
}

/**
 * Jobs that were in flight and have just landed, held for a beat.
 *
 * Server state has no "just now": a row is retrieving on one poll and verified
 * on the next, and a chip that vanished on that boundary never said how the job
 * ended. FR-14 sequences the tray as Signing in → Retrieving → Verifying →
 * ✓ Collected, so the last word has to be on screen long enough to be read.
 *
 * Only rows this component *watched* in flight are held. A row that was already
 * finished when the page loaded is history, not news.
 */
function useJustLanded(requirements: Requirement[]): Requirement[] {
  const watched = useRef(new Set<string>());
  const [held, setHeld] = useState<Held[]>([]);

  useEffect(() => {
    const landed: Requirement[] = [];
    for (const req of requirements) {
      if (isInFlight(req)) {
        watched.current.add(req.id);
      } else if (watched.current.has(req.id)) {
        watched.current.delete(req.id);
        landed.push(req);
      }
    }
    if (landed.length === 0) return;
    const until = Date.now() + HOLD_MS;
    setHeld((current) => [...current, ...landed.map((requirement) => ({ requirement, until }))]);
  }, [requirements]);

  // One timer for the soonest expiry, re-armed whenever the held set changes.
  // Not a timer per landing cancelled on re-render: the list is re-fetched
  // every few seconds while anything is in flight, and a cleanup that cleared
  // the timer on each poll would hold the chip forever.
  useEffect(() => {
    if (held.length === 0) return;
    const soonest = Math.min(...held.map((h) => h.until));
    const timer = window.setTimeout(
      () => setHeld((current) => current.filter((h) => h.until > soonest)),
      Math.max(0, soonest - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [held]);

  return held.map((h) => h.requirement);
}

/**
 * Shape as well as colour, because colour alone fails WCAG 1.4.1 (NFR-2): a
 * pulse while in flight, a tick once collected, an open ring when the next move
 * is the borrower's.
 */
function Beat({ req }: { req: Requirement }) {
  if (isInFlight(req)) {
    return <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-verity-light" />;
  }
  if (req.state === "verified") {
    return (
      <span aria-hidden className="text-[10px] leading-none font-bold text-ok-500">
        ✓
      </span>
    );
  }
  return <span aria-hidden className="size-1.5 rounded-full border border-chrome-ink" />;
}

/**
 * The dark strip under the header: what is moving right now, and what just
 * stopped.
 *
 * **Only work in flight, plus the beat where it lands.** FR-14 defines this
 * strip as the narration of retrieval, and FR-59 keeps a set-aside document out
 * of it: a parked item is the opposite of in flight, and one strip carrying
 * both leaves neither word meaning anything. A set-aside document lives on the
 * loan-file card instead — the list the borrower already has, and the one they
 * can act on.
 *
 * **Not a progress indicator.** The loan-file card is the single progress
 * artifact (v3 note 16) — it posts into the thread on every completion, so the
 * freshest count is always the most recent thing a borrower sees. A running
 * tally here as well would leave two of them, and a reader having to work out
 * which is authoritative.
 *
 * Chips wrap rather than scroll, so nothing sits off the right edge of a phone.
 */
export function TaskStrip({ requirements }: TaskStripProps) {
  const inFlight = requirements.filter(isInFlight);
  // A job retried inside the hold is in flight again; the chip follows the job.
  const landed = useJustLanded(requirements).filter((r) => !inFlight.some((f) => f.id === r.id));
  const rows = [...inFlight, ...landed];

  // An empty bar is chrome that earns no space on a phone.
  if (rows.length === 0) return null;

  return (
    <div className="flex shrink-0 flex-col gap-2.5 bg-chrome-900 px-4 py-2.5">
      <div className="flex flex-wrap gap-2">
        {rows.map((req) => (
          <span
            key={req.id}
            className="flex items-center gap-2 rounded-full bg-chrome-800 py-1.5 pr-3 pl-1.5"
          >
            {/* Verity's mark, not the source's initials — those duplicate the
                label right beside them and read as "ADADP". */}
            <span
              className="grid size-6 place-items-center rounded-full"
              style={{ background: "var(--brand-grad)" }}
              aria-hidden
            >
              <VerityMark className="size-3.5 [&_path]:fill-white [&_circle]:fill-indigo-450" />
            </span>
            {/* What the document is, not who supplies it. `source` reads as
                an identity for "ADP" or "your bank" only by coincidence — for
                anything the borrower supplies it is "You", which names
                nothing, and two such chips are indistinguishable. */}
            <span className="text-xs font-bold text-chrome-ink">{req.label}</span>
            <Beat req={req} />
            <span
              className={`font-mono text-[10px] tracking-wide uppercase ${
                isInFlight(req)
                  ? "text-ink-350"
                  : req.state === "verified"
                    ? "text-ok-500"
                    : "text-chrome-ink"
              }`}
            >
              {word(req)}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
